import type { JSX } from 'react';
import type { ContributingHazard } from '@tsr/shared-types';
import {
  contributingHazardTypeLabel,
  contributingHazardIcon,
  contributingHazardDotColor,
  contributingHazardSeverityText,
  contributingHazardSeverityTooltip,
} from '../lib/contributingHazards';

/**
 * #427c — the incident drill-in "Contributing hazards" section: the incident's HAZARD
 * COMPOSITION (which hazard fronts contribute, at what contributing SEVERITY). This is
 * NOT a per-hazard Response Level — Ontario IMS §5 Table 1 gives the INCIDENT ONE Response
 * Level (the header max-fold, the only "Response Level" on any surface). Each chip is
 * hazard TYPE + a contributing SEVERITY dot that reuses the LOCKED Response Level palette
 * for the DOT COLOUR ONLY; the operator-facing word is "severity", never "Response
 * Level"/"RL" (see contributingHazards.lock.test.ts). Renders nothing when empty
 * (quiet-COP). Evacuations are protective actions (Ontario IMS), surfaced in the
 * Protective Actions block — NOT here.
 */
export function ContributingHazardsSection({
  hazards,
}: {
  hazards: ContributingHazard[];
}): JSX.Element | null {
  if (hazards.length === 0) return null;
  return (
    <div data-testid="incident-contributing-hazards" className="space-y-1.5">
      <h4 className="text-[10px] uppercase tracking-wider text-slate-400">Contributing Hazards</h4>
      <ul className="flex flex-wrap gap-1.5">
        {hazards.map((h) => (
          <li
            key={`${h.source_kind}:${h.source_id}`}
            data-testid="contributing-hazard-chip"
            title={`${contributingHazardTypeLabel(h)} · ${contributingHazardSeverityTooltip(h)}`}
            className="flex items-center gap-1.5 rounded border border-slate-600 bg-slate-800/60 px-1.5 py-0.5 text-[11px] text-slate-200"
          >
            <span aria-hidden>{contributingHazardIcon(h)}</span>
            <span className="font-medium">{contributingHazardTypeLabel(h)}</span>
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: contributingHazardDotColor(h) }}
              aria-hidden
            />
            <span className="text-[10px] text-slate-400" data-testid="contributing-hazard-severity">
              {contributingHazardSeverityText(h)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
